import React, { PropsWithoutRef, useEffect, useState } from "react";
import {
  Drawer,
  Toolbar,
  List,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Box,
  IconButton,
  Tooltip,
  Collapse,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import MenuIcon from "@mui/icons-material/Menu";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { SIDEBAR_WIDTH } from "../constants";

import DashboardIcon from "@mui/icons-material/Dashboard";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import DirectionsCarFilledIcon from "@mui/icons-material/DirectionsCarFilled";
import PaymentsIcon from "@mui/icons-material/Payments";
import PaidIcon from "@mui/icons-material/Paid";
import NotificationsIcon from "@mui/icons-material/Notifications";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import SourceIcon from "@mui/icons-material/Source";
import SettingsApplicationsIcon from "@mui/icons-material/SettingsApplications";

interface Props {
  mobileOpen: boolean;
  handleSidebarToggle: () => void;
}

/* ---------------- CONFIG ---------------- */

const sidebarConfig = [
  {
    id: "dashboard",
    label: "Dashboard",
    icon: <DashboardIcon />,
    path: "/dashboard",
  },
  {
    id: "customers",
    label: "Customer Management",
    icon: <PeopleAltIcon />,
    path: "/manage-users",
  },
  {
    id: "dealers",
    label: "Dealers Management",
    icon: <WarehouseIcon />,
    path: "/manage-dealers",
  },
  {
    id: "categories",
    label: "Categories Management",
    icon: <img src="/static/images/logs_icon.svg" alt="Icon" />,
    path: "/manage-categories",
  },
  {
    id: "listings",
    label: "Listings Management",
    icon: <DirectionsCarFilledIcon />,
    children: [
      { label: "Listed Vehicles", path: "/manage-vehicles" },
      { label: "Listed Spare Parts", path: "/manage-spare-parts" },
      { label: "Listed Car Accessories", path: "/manage-car-accessories" },
    ],
  },
  {
    id: "specification",
    label: "Vehicle Specification",
    icon: <SettingsApplicationsIcon />,
    children: [
      { id: "brands", label: "Manage Brands", path: "/manage-brands" },
      { id: "model", label: "Manage Model", path: "/manage-model" },
      { id: "vehicle-type", label: "Manage Vehicle Type", path: "/manage-vehicle-type" },
      { id: "fuel-type", label: "Manage Fuel Type", path: "/manage-fuel-type" },
      { id: "engine-power", label: "Manage Engine Power", path: "/manage-engine-power" },
      { id: "transmission", label: "Manage Transmission", path: "/manage-transmission" },
      { id: "drive-type", label: "Manage Drive Type", path: "/manage-drive-type" },
      { id: "emissions-class", label: "Manage Emissions Class", path: "/manage-emissions-class" },
      { id: "environmental-badge", label: "Manage Environmental Badge", path: "/manage-environmental-badge" },
      { id: "damage-type", label: "Manage Damage Type", path: "/manage-damage-type" },
      { id: "condition", label: "Manage Condition", path: "/manage-condition" },
      { id: "axles", label: "Manage Axles", path: "/manage-axles" },
      { id: "color", label: "Manage Color", path: "/manage-color" },
      { id: "features", label: "Manage Features", path: "/manage-features" },
    ],
  },
  {
    id: "packages",
    label: "Manage Packages",
    icon: <PaidIcon />,
    path: "/manage-packages",
  },
  {
    id: "revenue",
    label: "Revenue Management",
    icon: <PaymentsIcon />,
    path: "/manage-revenue",
  },
  {
    id: "analytics",
    label: "Report & Analytics",
    icon: <AnalyticsIcon />,
    path: "/manage-analytics",
  },
  {
    id: "notifications",
    label: "Notification Management",
    icon: <NotificationsIcon />,
    path: "/manage-notifications",
  },
  {
    id: "subadmin",
    label: "Sub-Admin Management",
    icon: <ManageAccountsIcon />,
    path: "/manage-subadmin",
  },
  {
    id: "blogs",
    label: "Blog Management",
    icon: <SourceIcon />,
    children: [
      { id: "blogs-listings", label: "Blog Listings", path: "/manage-blogs-listings" },
      { id: "blogs-categories", label: "Blog Categories", path: "/manage-blogs-categories" },
    ],
  },
  {
    id: "cms",
    label: "CMS Management",
    icon: <img src="/static/images/cms_icon.svg" alt="Icon" />,
    path: "/manage-cms",
  },
];

/* ---------------- COMPONENT ---------------- */

export default function Sidebar({
  mobileOpen,
  handleSidebarToggle,
}: PropsWithoutRef<Props>) {
  const location = useLocation();
  const navigate = useNavigate();

  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({});

  const isRouteActive = React.useCallback(
    (path: string) =>
      location.pathname === path ||
      location.pathname.startsWith(path),
    [location.pathname]
  );

  const isAnyChildActive = React.useCallback(
    (children: any[]) => children.some((child) => isRouteActive(child.path)),
    [isRouteActive]
  );

  /* Auto expand active menus */
  useEffect(() => {
    const updated: Record<string, boolean> = {};

    sidebarConfig.forEach((item) => {
      if (item.children) {
        updated[item.id] = isAnyChildActive(item.children);
      }
    });

    setOpenMenus(updated);
  }, [location.pathname, isAnyChildActive]);

  const drawer = (
    <>
      {/* LOGO */}
      <Toolbar className="sidebar-logo" sx={{ position: "relative" }}>
        <a
          href="/dashboard"
          onClick={(e) => {
            e.preventDefault();
            navigate("/dashboard");
          }}
          className="logo_link"
        >
          <figure>
            <figcaption>Car Advertising</figcaption>
          </figure>
        </a>

        <IconButton
          sx={{ display: "none", position: "absolute", top: 10, right: 10 }}
          onClick={handleSidebarToggle}
        >
          <MenuIcon />
        </IconButton>
      </Toolbar>

      <List sx={{ flexGrow: 1 }} className="sidebr-lst">
        {sidebarConfig.map((item) => {
          const hasChildren = !!item.children;

          const isActive = item.path
            ? isRouteActive(item.path)
            : hasChildren
              ? isAnyChildActive(item.children!)
              : false;

          const isOpen = openMenus[item.id] || false;

          return (
            <React.Fragment key={item.id}>
              <ListItemButton
                className="lst-itm"
                selected={isActive}
                onClick={() => {
                  if (hasChildren) {
                    setOpenMenus((prev) => ({
                      ...prev,
                      [item.id]: !prev[item.id],
                    }));
                  } else if (item.path) {
                    navigate(item.path);
                  }
                }}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText className="lstitm-txt" primary={item.label} />
                {hasChildren && (isOpen ? <ExpandLess /> : <ExpandMore />)}
              </ListItemButton>

              {hasChildren && (
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.children!.map((child) => (
                      <Tooltip
                        key={child.path}
                        title={child.label}
                        placement="right"
                      >
                        <ListItemButton
                          selected={isRouteActive(child.path)}
                          onClick={() => navigate(child.path)}
                          sx={{ pl: 4 }}
                        >
                          <ListItemText
                            className="lstitm-txt"
                            primary={child.label}
                          />
                        </ListItemButton>
                      </Tooltip>
                    ))}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          );
        })}
      </List>
    </>
  );

  return (
    <Box
      component="nav"
      sx={{
        width: mobileOpen ? SIDEBAR_WIDTH : 0,
        transition: "width 0.3s",
        overflowX: "hidden",
      }}
      className={mobileOpen ? "sidebar-main-div active" : "sidebar-main-div"}
    >
      <Drawer
        className="sidebar_drawer"
        variant="persistent"
        sx={{
          "& .MuiDrawer-paper": {
            width: SIDEBAR_WIDTH,
            boxSizing: "border-box",
          },
        }}
        open
      >
        {drawer}
      </Drawer>
    </Box>
  );
}